import React, {useRef, useState } from "react";
import { useStoreDispatch } from "../../store/hooks";
import { setBasicDialogOpenFor, setOpenToast } from "../../store/helpers/helperReducers";
import { Sheet } from "../ui/sheet";
import { Button } from "../ui/button";
import { Info, Loader2, X } from "lucide-react";
import { useUploadCustomAuditTemplateMutation } from "../../store/api/tasks/taskApiSlice";
import {
  CUSTOM_AUDIT_XML_ONLY_MESSAGE,
  extractCustomAuditUploadErrorMessage,
  resolveCustomAuditTemplateFileType,
} from "./customAuditTemplateUploadUtils";
import CustomAuditWildcardTooltip from "./CustomAuditWildcardTooltip";
import FileUpload from "../ui/file-upload";

interface CustomAuditTemplateImportProps {
  modelOpenMode?: string;
}

const CustomAuditTemplateImport: React.FC<CustomAuditTemplateImportProps> = ({
  modelOpenMode = "Apply",
}) => {
  const dispatch = useStoreDispatch();
  const [uploadCustomAuditTemplate, { isLoading, reset }] = useUploadCustomAuditTemplateMutation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const hiddenFileInput = useRef<HTMLInputElement | null>(null);

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (hiddenFileInput.current) {
      hiddenFileInput.current.value = "";
    }
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    clearSelectedFile();
    setDescription("");
    reset();
    dispatch(setBasicDialogOpenFor(undefined));
  };
  const handleImport = async () => {
    if (!selectedFile) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Please select an XML file." }));
      return;
    }

    const fileType = resolveCustomAuditTemplateFileType(selectedFile);
    if (fileType === null) {
      clearSelectedFile();
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: CUSTOM_AUDIT_XML_ONLY_MESSAGE }));
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response: any = await uploadCustomAuditTemplate({ formData, fileType, description }).unwrap();
      const templateName = typeof response?.templateName === "string" && response.templateName.trim().length > 0
        ? response.templateName.trim()
        : selectedFile.name;

      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: `Template "${templateName}" uploaded successfully.`,
        })
      );

      clearSelectedFile();
      setDescription("");
      reset();
      dispatch(setBasicDialogOpenFor(undefined));
    } catch (error: any) {
      clearSelectedFile();
      reset();
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: extractCustomAuditUploadErrorMessage(error),
        })
      );
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button disabled={!selectedFile || isLoading} onClick={handleImport}>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
        Upload
      </Button>
    </>
  );

  return (
    <Sheet open={true} onClose={handleClose} title={`${modelOpenMode} Custom Audit Template`} footer={footer} width="520px">
      <div className="space-y-2 pt-2">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">Upload a Custom Audit Template</p>
            <CustomAuditWildcardTooltip>
              <button
                type="button"
                aria-label="Supported wildcards for custom audit templates"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-border/70 bg-background text-muted-foreground shadow-sm transition-all hover:border-primary/40 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
              >
                <Info className="h-4 w-4" />
              </button>
            </CustomAuditWildcardTooltip>
          </div>
          <p className="text-xs text-muted-foreground">Only XML templates are supported for Custom Audit upload.</p>
        </div>
        <div className="w-full mt-0 mb-4">
          <FileUpload
            file={selectedFile}
            setFile={setSelectedFile}
            accept=".xml,application/xml,text/xml"
            allowedTypes={["text/xml", "application/xml"]}
            maxSizeMB={10}
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-foreground" htmlFor="custom-audit-template-description-input">
            Description (optional)
          </label>
          <textarea
            id="custom-audit-template-description-input"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={3}
            maxLength={500}
            className="flex w-full rounded-md border border-input bg-transparent text-foreground px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-y"
            placeholder="Add a brief description for this template"
          />
        </div>
      </div>
    </Sheet>
  );
};

export default CustomAuditTemplateImport;